import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  HallOption,
  approveRejectHallBooking,
  fetchMasterOptions,
  fieldOf,
  findOneHallBooking,
  formatDateToDMY,
  getLoggedInEmpId,
  getRequestStatusUi,
  resolveOptionLabel,
} from "../otherServices/communityHall/communityHall.service";

const PAGE_TITLE = "View Booking of Community Hall/Common Room/Park";

const HallBookingApproval: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { requestID } = useParams<{ requestID: string }>();
  const refId = requestID ? decodeURIComponent(requestID) : "";
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [confirmReject, setConfirmReject] = useState(false);
  const [requestTypes, setRequestTypes] = useState<HallOption[]>([]);
  const [staffQuarters, setStaffQuarters] = useState<HallOption[]>([]);

  const field = (...keys: string[]) => fieldOf(details, ...keys);

  useEffect(() => {
    fetchMasterOptions()
      .then((options) => {
        setRequestTypes(options.requestTypes);
        setStaffQuarters(options.staffQuarters);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneHallBooking(refId);
        if (!data) {
          setDetails(null);
          toast.error(label("failed_to_load_details", "Failed to load details"));
          return;
        }
        setDetails(data);
      } catch (err: any) {
        setDetails(null);
        toast.error(Api.getErrorMessage(err) || label("failed_to_load_details", "Failed to load details"));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const requestTypeCode = field("RequestType", "requestType");
  const staffQuarterCode = field("StaffQuarter", "staffQuarter");
  const statusUi = getRequestStatusUi(field("RequestStatus", "requestStatus"));
  const currentEmpId = getLoggedInEmpId();
  const remarksList: any[] = useMemo(() => {
    const list = (details?.ToRemarks?.length ? details.ToRemarks : details?.RemarksHistory) ?? [];
    return (Array.isArray(list) ? list : []).filter((item: any) => String(item?.ApproverUserID || "") !== currentEmpId);
  }, [details, currentEmpId]);

  const submit = async (action: "A" | "R") => {
    setSubmitting(true);
    try {
      const result = await approveRejectHallBooking({
        Action: action,
        ReferenceNumber: refId,
        EmpID: field("EmpID", "empId"),
        ApproverUserID: field("ApproverUserID", "approverUserID"),
        HRemarks: remarks,
        isCancellation: !!field("AReferenceNumber"),
      });
      if (!result.ok) {
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
        return;
      }
      toast.success(
        result.message ||
          (action === "A" ? label("Application has been approved", "Application has been approved") : label("Application has been rejected", "Application has been rejected"))
      );
      navigate(routes.todoWorklist.tasks);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setSubmitting(false);
      setConfirmReject(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">{label("view_booking_of_community_hall_common_room_park", PAGE_TITLE)}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.todoWorklist.tasks}>To-Do Worklist</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{label("view_booking_of_community_hall_common_room_park", PAGE_TITLE)}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
            <i className="ti ti-arrow-left me-1"></i>
            {label("back", "Back")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : !details ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">{label("no_data_found", "No data found")}</div>
          </div>
        ) : (
          <>
            <div className="alert alert-light d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{label("request_status", "Request Status")}</span>
              <span className={statusUi.badge}>{statusUi.label}</span>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{label("application_info", "Application Info")}</div>
              <div className="card-body">
                <div className="row">
                  <Info label={label("reference_no", "Reference No")} value={field("ReferenceNumber", "referenceNumber", "id") || refId} />
                  <Info label={label("associated_refrence_no", "Associated Reference No")} value={field("AReferenceNumber") || "-"} />
                  <Info label={label("residential_address_employee", "Residential address of Employee")} value={field("ResidentialAddressOfEmployee", "residentialAddressOfEmployee")} />
                  <Info label={label("mobile_number", "Mobile Number")} value={field("ContactNumber", "contactNumber")} />
                  <Info label={label("booking_start_date", "Booking Begin Date")} value={field("BookingBeginDateDisplay", "bookingBeginDate", "BookingBeginDate")} />
                  <Info label={label("booking_end_date", "Booking End Date")} value={field("BookingEndDateDisplay", "bookingEndDate", "BookingEndDate")} />
                  <Info label={label("total_booking_amount", "Total amount of booking")} value={field("BookingAmount")} />
                  <Info label={label("gst_amount", "GST amount")} value={field("GstAmount")} />
                  <Info label={label("total_amount", "Total amount")} value={field("TotalAmount")} />
                  <Info label={label("total_days_of_booking", "Total days of booking")} value={field("TotalDaysOfBooking")} />
                  <Info label={label("request_type", "Request Type")} value={resolveOptionLabel(requestTypes, requestTypeCode) || requestTypeCode} />
                  <Info label={label("staff_quarter", "Staff Quarters")} value={resolveOptionLabel(staffQuarters, staffQuarterCode) || staffQuarterCode} />
                  <Info label={t("purpose")} value={field("Purpose", "purpose")} />
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{label("approver_remarks", "Approver Remarks")}</div>
              <div className="card-body">
                <label className="form-label fw-semibold">{t("remarks")}</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder={`${label("write_something", "Write something")}...`}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{label("previous_remarks", "Previous Remarks")}</div>
              <div className="card-body">
                {remarksList.length === 0 ? (
                  <div className="text-muted">{label("no_data_found", "No data found")}</div>
                ) : (
                  remarksList.map((item: any, index: number) => (
                    <div className="border rounded p-3 mb-2" key={`${item?.ApproverUserID || "remark"}-${index}`}>
                      <div className="fw-semibold">{item?.ApproverName || "-"}</div>
                      <div className="small text-muted">{item?.ApproverUserID || "-"} · {formatDateToDMY(item?.TimeStamp)}</div>
                      <div className="mt-2">{item?.Remarks || "-"}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-danger" disabled={submitting} onClick={() => setConfirmReject(true)}>
                {label("reject", "Reject")}
              </button>
              <button type="button" className="btn btn-success" disabled={submitting} onClick={() => submit("A")}>
                {label("approve", "Approve")}
              </button>
            </div>
          </>
        )}
      </div>

      {confirmReject && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">{label("reject", "Reject")}</h5></div>
              <div className="modal-body">{label("are_you_sure_want_to_reject", "Are you sure you want to reject?")}</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setConfirmReject(false)}>{label("no", "No")}</button>
                <button type="button" className="btn btn-danger" disabled={submitting} onClick={() => submit("R")}>{label("yes", "Yes")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="col-md-6 mb-3">
    <div className="text-muted small fw-semibold">{label}</div>
    <div className="fw-medium">{value || "-"}</div>
  </div>
);

export default HallBookingApproval;
